import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Eraser, FileX, FolderX, type LucideIcon, PackageX, RotateCcw } from "lucide-react";
import { type ComponentProps, type ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import type { RouterInputs } from "@/lib/types";
import { cn, relativeTime } from "@/lib/utils";

type SettingKey = RouterInputs["settings"]["set"]["key"];
type Changed = { updatedAt: string | Date; updatedByName: string | null };

export const Route = createFileRoute("/_app/admin/settings")({
  beforeLoad: ({ context }) => {
    // A UX guard, not a security boundary — `adminProcedure` is the real one.
    if (context.user?.role !== "admin") throw redirect({ to: "/devices" });
  },
  component: SettingsPage,
});

/** The in-page section nav, in page order. Each id is a card's `id`. */
const SECTIONS = [
  {
    label: "Reservations",
    items: [
      { id: "reservation-lifetime", label: "Reservation lifetime" },
      { id: "idle-timeout", label: "Idle timeout" },
      { id: "max-session-length", label: "Maximum session length" },
    ],
  },
  {
    label: "Cleanup between users",
    items: [
      { id: "reset-steps", label: "Reset steps" },
      { id: "clear-allow", label: "Only clear these apps" },
      { id: "clear-deny", label: "Never clear these apps" },
      { id: "cleanup-deadline", label: "Cleanup deadline" },
    ],
  },
] as const;

const SECTION_IDS: string[] = SECTIONS.flatMap((group) => group.items.map((item) => item.id));

function SettingsPage() {
  const qc = useQueryClient();
  const { data } = useQuery(trpc.settings.get.queryOptions());
  const [active, jumpTo] = useActiveSection(SECTION_IDS, Boolean(data));

  const save = useMutation(
    trpc.settings.set.mutationOptions({
      onSuccess: () => {
        toast.success("Saved");
        qc.invalidateQueries({ queryKey: trpc.settings.get.queryKey() });
        qc.invalidateQueries({ queryKey: trpc.settings.public.queryKey() });
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  const changedAt = (key: SettingKey) => data?.changed.find((row) => row.key === key);
  const cleanupEnabled = data?.values["cleanup.enabled"] ?? false;
  const clearAppData = data?.values["cleanup.clearAppData"] ?? false;

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Settings"
        description="Farm-wide policy. Changes take effect within a few seconds — no restart, and no redeploy."
      />

      {data && (
        <div className="xl:grid xl:grid-cols-[208px_minmax(0,1fr)] xl:items-start xl:gap-8">
          {/* Drawn like the sub-nav Vercel's settings put in its sidebar — text-only
              36px rows, the current one filled — with the sidebar's group rule
              between the two sections, whose names the headings beside it carry.
              Only from xl: below that it would squeeze the cards it indexes. */}
          <nav aria-label="Settings sections" className="sticky top-0 hidden xl:grid">
            {SECTIONS.map((group, index) => (
              <div key={group.label} className="grid">
                {index > 0 && <div aria-hidden="true" className="mx-1 my-2 h-px bg-gray-200" />}
                {group.items.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    aria-current={active === item.id ? "true" : undefined}
                    onClick={(event) => {
                      event.preventDefault();
                      jumpTo(item.id);
                    }}
                    className={cn(
                      "flex h-9 items-center truncate rounded-md px-2.5 text-label-14 transition-colors",
                      active === item.id
                        ? "bg-gray-200 text-gray-1000"
                        : "text-gray-900 hover:bg-gray-alpha-100 hover:text-gray-1000",
                    )}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            ))}
          </nav>

          <div className="grid min-w-0 gap-14">
            <SettingsGroup title="Reservations">
              <DurationSetting
                id="reservation-lifetime"
                inputId="ttl"
                label="Reservation lifetime"
                help="How long a reservation survives without the browser renewing it. The tab renews every third of this, so it is the window in which a closed laptop keeps a device."
                minutes={data.values["reservation.ttlSeconds"] / 60}
                pending={save.isPending}
                onSave={(minutes) =>
                  minutes !== null &&
                  save.mutate({ key: "reservation.ttlSeconds", value: minutes * 60 })
                }
                changed={changedAt("reservation.ttlSeconds")}
              />

              <DurationSetting
                id="idle-timeout"
                inputId="idle"
                label="Idle timeout"
                help="Release a device nobody has touched for this long. Interaction counts whether it came through the browser or straight from adb — the provider reports it either way. Empty turns it off."
                minutes={secondsToMinutes(data.values["reservation.idleTimeoutSeconds"])}
                optional
                pending={save.isPending}
                onSave={(minutes) =>
                  save.mutate({
                    key: "reservation.idleTimeoutSeconds",
                    value: minutes === null ? null : minutes * 60,
                  })
                }
                changed={changedAt("reservation.idleTimeoutSeconds")}
              />

              <DurationSetting
                id="max-session-length"
                inputId="max"
                label="Maximum session length"
                help="A hard cap on one reservation, however busy it is. Empty turns it off."
                minutes={secondsToMinutes(data.values["reservation.maxDurationSeconds"])}
                optional
                pending={save.isPending}
                onSave={(minutes) =>
                  save.mutate({
                    key: "reservation.maxDurationSeconds",
                    value: minutes === null ? null : minutes * 60,
                  })
                }
                changed={changedAt("reservation.maxDurationSeconds")}
              />
            </SettingsGroup>

            <SettingsGroup title="Cleanup between users">
              <SettingCard
                id="reset-steps"
                title="Reset steps"
                description="The provider always checks protected preloads before returning a device to the pool. Turn this on to also run the configurable reset steps below."
                hint="Each switch saves as soon as you flip it."
              >
                {/* The master switch heads the steps it governs, the way Vercel's
                    notification channels sit in one list under a header strip. */}
                <div className="overflow-hidden rounded-md shadow-border">
                  <ToggleSetting
                    id="cleanup-enabled"
                    label="Run ordinary reset steps when a reservation ends"
                    checked={cleanupEnabled}
                    pending={save.isPending}
                    onChange={(value) => save.mutate({ key: "cleanup.enabled", value })}
                    changed={changedAt("cleanup.enabled")}
                    className="bg-background-200"
                  />

                  {/* The steps stay visible when ordinary cleanup is off, greyed rather than
                      hidden: an admin turning this on needs to see what they are about
                      to switch on before they switch it on. */}
                  <div
                    className={cn(
                      "divide-y divide-gray-alpha-400 border-gray-alpha-400 border-t",
                      !cleanupEnabled && "pointer-events-none opacity-50",
                    )}
                  >
                    <ToggleSetting
                      id="cleanup-uninstall"
                      icon={PackageX}
                      label="Uninstall apps installed during the session"
                      help="Anything that appeared since the session started. Apps the device already had are left alone, and if the provider restarted mid-session it declines to guess."
                      checked={data.values["cleanup.uninstallApps"]}
                      pending={save.isPending}
                      onChange={(value) => save.mutate({ key: "cleanup.uninstallApps", value })}
                      changed={changedAt("cleanup.uninstallApps")}
                    />
                    <ToggleSetting
                      id="cleanup-screen"
                      icon={RotateCcw}
                      label="Reset the screen"
                      help="Back to the home screen, rotation upright, clipboard cleared."
                      checked={data.values["cleanup.resetScreen"]}
                      pending={save.isPending}
                      onChange={(value) => save.mutate({ key: "cleanup.resetScreen", value })}
                      changed={changedAt("cleanup.resetScreen")}
                    />
                    <ToggleSetting
                      id="cleanup-data"
                      icon={Eraser}
                      label="Clear app data"
                      help="Wipes the data of an app left on the device — accounts, caches, settings. Android only. Off by default because a device's third-party apps include anything preinstalled across your fleet, such as a test harness."
                      checked={clearAppData}
                      pending={save.isPending}
                      onChange={(value) => save.mutate({ key: "cleanup.clearAppData", value })}
                      changed={changedAt("cleanup.clearAppData")}
                    />
                    <ToggleSetting
                      id="cleanup-folders"
                      icon={FolderX}
                      label="Empty scratch folders"
                      help="Which folders is set per device in each provider's config, not here — these end in a recursive delete on a phone. A device with none configured skips this."
                      checked={data.values["cleanup.wipeFolders"]}
                      pending={save.isPending}
                      onChange={(value) => save.mutate({ key: "cleanup.wipeFolders", value })}
                      changed={changedAt("cleanup.wipeFolders")}
                    />
                  </div>
                </div>
              </SettingCard>

              {/* Its own card, not a step under the switch above: it is not
                  gated on ordinary cleanup, because one renter's files reaching
                  the next is a leak rather than a reset preference. */}
              <SettingCard
                id="uploads"
                title="Uploaded files"
                description="Files renters upload go into one Uploads folder the farm owns; photos and videos go into the device's photo library, through the Rent a Phone helper app on iPhones."
                hint="Saves as soon as you flip it."
              >
                <div className="overflow-hidden rounded-md shadow-border">
                  <ToggleSetting
                    id="cleanup-uploads"
                    icon={FileX}
                    label="Delete uploads and uploaded photos when a reservation ends"
                    help="Runs whether or not the reset steps above are on. Turn off only if devices are never shared between people who should not see each other's files."
                    checked={data.values["cleanup.deleteUploads"]}
                    pending={save.isPending}
                    onChange={(value) => save.mutate({ key: "cleanup.deleteUploads", value })}
                    changed={changedAt("cleanup.deleteUploads")}
                  />
                </div>
              </SettingCard>

              {/* The two lists scope "Clear app data", so they grey out with it — and
                  with ordinary cleanup as a whole, which that step sits under. */}
              <PatternSetting
                id="clear-allow"
                inputId="cleanup-data-allow"
                label="Only clear these apps"
                help={
                  <>
                    One app id per line. <Code>*</Code> matches anything, so <Code>*.google.*</Code>{" "}
                    covers every Google app. Leave this empty to clear every app on the device —
                    usually the wrong answer, because clearing a signed-in MDM agent or a test
                    harness breaks it for everyone afterwards.
                  </>
                }
                placeholder={"*.google.*\ncom.acme.appundertest"}
                patterns={data.values["cleanup.clearAppDataAllow"]}
                inactive={!cleanupEnabled || !clearAppData}
                pending={save.isPending}
                onSave={(value) => save.mutate({ key: "cleanup.clearAppDataAllow", value })}
                changed={changedAt("cleanup.clearAppDataAllow")}
              />
              <PatternSetting
                id="clear-deny"
                inputId="cleanup-data-deny"
                label="Never clear these apps"
                help="Checked after the list above and wins over it, so you can allow a whole prefix and carve one app out of it."
                placeholder="com.acme.mdm"
                patterns={data.values["cleanup.clearAppDataDeny"]}
                inactive={!cleanupEnabled || !clearAppData}
                pending={save.isPending}
                onSave={(value) => save.mutate({ key: "cleanup.clearAppDataDeny", value })}
                changed={changedAt("cleanup.clearAppDataDeny")}
              />

              <SecondsSetting
                id="cleanup-deadline"
                inputId="cleanup-timeout"
                label="Cleanup deadline"
                help="How long a provider may hold a device before giving up and returning it anyway. A partial reset is reported in the audit log."
                seconds={data.values["cleanup.timeoutSeconds"]}
                inactive={!cleanupEnabled}
                pending={save.isPending}
                onSave={(seconds) => save.mutate({ key: "cleanup.timeoutSeconds", value: seconds })}
                changed={changedAt("cleanup.timeoutSeconds")}
              />
            </SettingsGroup>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Which card the section nav should highlight, and a way to jump to one.
 *
 * Measured against the shell's scrolling `<main>`, not the window, because the
 * window never scrolls. The last card can be too short to ever reach the top,
 * so the bottom of the page counts as being on it.
 */
function useActiveSection(ids: string[], ready: boolean) {
  const [active, setActive] = useState(ids[0]);
  // A jump scrolls smoothly past every card in between; without this the
  // highlight would flicker through each of them on the way.
  const lockedUntil = useRef(0);

  useEffect(() => {
    if (!ready) return;
    const first = document.getElementById(ids[0] ?? "");
    const scroller = first?.closest("main");
    if (!scroller) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      if (Date.now() < lockedUntil.current) return;
      const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
      if (atBottom) {
        setActive(ids[ids.length - 1]);
        return;
      }
      const top = scroller.getBoundingClientRect().top + 96;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= top) current = id;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ids, ready]);

  const jumpTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    lockedUntil.current = Date.now() + (reduce ? 0 : 800);
    setActive(id);
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };

  return [active, jumpTo] as const;
}

function SettingsGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="grid gap-5">
      <h2 className="text-gray-1000 text-heading-20 sm:text-heading-24">{title}</h2>
      <div className="grid gap-8">{children}</div>
    </section>
  );
}

/**
 * A Vercel "General" settings card (Geist's Fieldset): title, description and
 * control on white, then a footer strip on the page colour with a hint on the
 * left and a 32px Save on the right. On a phone the footer stacks and Save
 * spans it.
 *
 * `inactive` greys a setting whose parent switch is off, the same way the
 * reset steps grey under theirs — the footer hint stays legible, because it is
 * what says why.
 */
function SettingCard({
  id,
  title,
  labelFor,
  description,
  changed,
  hint,
  action,
  inactive = false,
  children,
}: {
  id: string;
  title: string;
  /** The input the title labels, so clicking the title focuses it. */
  labelFor?: string;
  description: ReactNode;
  changed?: Changed;
  hint: ReactNode;
  action?: ReactNode;
  inactive?: boolean;
  children: ReactNode;
}) {
  return (
    <Card
      id={id}
      role="region"
      aria-labelledby={`${id}-title`}
      className="scroll-mt-6 gap-0 rounded-lg py-0"
    >
      <div className={cn("grid gap-4 p-5 sm:p-6", inactive && "pointer-events-none opacity-50")}>
        <div className="grid gap-4">
          <h3 id={`${id}-title`} className="text-gray-1000 text-heading-20">
            {labelFor ? <label htmlFor={labelFor}>{title}</label> : title}
          </h3>
          <div className="grid gap-2">
            <CardDescription>{description}</CardDescription>
            <ChangedNote changed={changed} />
          </div>
        </div>
        {children}
      </div>
      <CardFooter className="mb-0 flex-col items-stretch gap-3 px-5 sm:flex-row sm:items-center sm:gap-4 sm:px-6">
        <p className="min-w-0">{hint}</p>
        {action && (
          <div
            className={cn("sm:ml-auto sm:shrink-0", inactive && "pointer-events-none opacity-50")}
          >
            {action}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}

function SaveButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <Button
      size="small"
      className="w-full pointer-coarse:h-10 sm:w-auto"
      disabled={disabled}
      onClick={onClick}
    >
      Save
    </Button>
  );
}

/** Geist's inline code: mono at 6/7 of the text around it, in a hairline chip. */
function Code({ children }: { children: ReactNode }) {
  return (
    <code className="whitespace-nowrap rounded-md border border-gray-alpha-400 bg-gray-100 px-1 py-px font-mono text-[0.857em] text-gray-1000">
      {children}
    </code>
  );
}

/**
 * One switch as a row of Vercel's notification-channel list: an optional
 * 32px icon disc, a 14/500 name over a grey line of help, the switch at the
 * right edge.
 */
function ToggleSetting({
  id,
  icon: Icon,
  label,
  help,
  checked,
  pending,
  onChange,
  changed,
  className,
}: {
  id: string;
  icon?: LucideIcon;
  label: string;
  help?: string;
  checked: boolean;
  pending: boolean;
  onChange: (value: boolean) => void;
  changed?: Changed;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-4 px-4 py-4", className)}>
      {Icon && (
        <span
          aria-hidden="true"
          className="hidden size-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-1000 shadow-border sm:flex"
        >
          <Icon className="size-4" />
        </span>
      )}
      <div className="grid min-w-0 flex-1 gap-0.5">
        <Label htmlFor={id} className="w-fit font-medium text-gray-1000 text-label-14 leading-5">
          {label}
        </Label>
        {help && <p className="text-copy-14 text-gray-900">{help}</p>}
        <ChangedNote changed={changed} />
      </div>
      <Switch id={id} checked={checked} disabled={pending} onCheckedChange={onChange} />
    </div>
  );
}

/**
 * A list of app id globs, edited as one pattern per line.
 *
 * A textarea rather than a tag input because these are pasted from somewhere
 * else as often as they are typed, and because the list is read far more often
 * than it is edited — an admin checking what the policy covers should be able
 * to see all of it at once.
 */
function PatternSetting({
  id,
  inputId,
  label,
  help,
  placeholder,
  patterns,
  inactive,
  pending,
  onSave,
  changed,
}: {
  id: string;
  inputId: string;
  label: string;
  help: ReactNode;
  placeholder: string;
  patterns: string[];
  inactive: boolean;
  pending: boolean;
  onSave: (patterns: string[]) => void;
  changed?: Changed;
}) {
  const [draft, setDraft] = useState(patterns.join("\n"));

  useEffect(() => {
    setDraft(patterns.join("\n"));
  }, [patterns]);

  // Blank lines are dropped rather than rejected: a trailing newline is how
  // people type a list, not a mistake.
  const parsed = draft
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const dirty = parsed.join("\n") !== patterns.join("\n");

  return (
    <SettingCard
      id={id}
      title={label}
      labelFor={inputId}
      description={help}
      changed={changed}
      inactive={inactive}
      hint="Only applies while Clear app data is on."
      action={<SaveButton disabled={pending || !dirty} onClick={() => onSave(parsed)} />}
    >
      <Textarea
        id={inputId}
        rows={3}
        spellCheck={false}
        className="text-copy-13-mono pointer-coarse:text-base sm:max-w-[600px]"
        placeholder={placeholder}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
    </SettingCard>
  );
}

/**
 * A duration in seconds rather than minutes.
 *
 * The reservation policies above are all human-scale — "half an hour" — but a
 * cleanup deadline is a machine one, and rounding it to minutes would make the
 * difference between 90 and 120 seconds unexpressible.
 */
function SecondsSetting({
  id,
  inputId,
  label,
  help,
  seconds,
  inactive,
  pending,
  onSave,
  changed,
}: {
  id: string;
  inputId: string;
  label: string;
  help: string;
  seconds: number;
  inactive: boolean;
  pending: boolean;
  onSave: (seconds: number) => void;
  changed?: Changed;
}) {
  const [draft, setDraft] = useState(String(seconds));

  useEffect(() => {
    setDraft(String(seconds));
  }, [seconds]);

  const parsed = Number(draft);
  const invalid = !Number.isInteger(parsed) || parsed < 10 || parsed > 600;
  const dirty = parsed !== seconds;

  return (
    <SettingCard
      id={id}
      title={label}
      labelFor={inputId}
      description={help}
      changed={changed}
      inactive={inactive}
      hint={
        inactive
          ? "Turn on ordinary reset steps to change this."
          : "A whole number between 10 and 600 seconds."
      }
      action={<SaveButton disabled={pending || invalid || !dirty} onClick={() => onSave(parsed)} />}
    >
      <UnitInput
        id={inputId}
        unit="seconds"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
    </SettingCard>
  );
}

/** A number with its unit in Geist's grey affix, as Vercel's "vercel.com/" prefix. */
function UnitInput({ unit, ...props }: ComponentProps<typeof Input> & { unit: string }) {
  return (
    <Input
      inputMode="numeric"
      className="text-label-14 tabular-nums sm:w-[300px]"
      suffix={<span className="text-gray-700">{unit}</span>}
      {...props}
    />
  );
}

function ChangedNote({ changed }: { changed?: Changed }) {
  if (!changed) return null;
  return (
    <p className="text-gray-900 text-label-13">
      Changed {relativeTime(changed.updatedAt)}
      {changed.updatedByName ? ` by ${changed.updatedByName}` : ""}
    </p>
  );
}

function secondsToMinutes(seconds: number | null) {
  return seconds === null ? null : seconds / 60;
}

/**
 * One duration, in minutes.
 *
 * Minutes rather than seconds because every one of these is a human-scale
 * policy — "half an hour", not "1800" — and the wire keeps seconds so the
 * server never has to guess at a unit.
 */
function DurationSetting({
  id,
  inputId,
  label,
  help,
  minutes,
  optional,
  pending,
  onSave,
  changed,
}: {
  id: string;
  inputId: string;
  label: string;
  help: string;
  minutes: number | null;
  optional?: boolean;
  pending: boolean;
  onSave: (minutes: number | null) => void;
  changed?: Changed;
}) {
  const [draft, setDraft] = useState(minutes === null ? "" : String(minutes));

  // The saved value is the source of truth: a refetch (or another admin's
  // change) must not be masked by a stale draft nobody submitted.
  useEffect(() => {
    setDraft(minutes === null ? "" : String(minutes));
  }, [minutes]);

  const parsed = draft.trim() === "" ? null : Number(draft);
  const invalid = parsed === null ? !optional : !Number.isFinite(parsed) || parsed <= 0;
  const dirty = parsed !== minutes;

  return (
    <SettingCard
      id={id}
      title={label}
      labelFor={inputId}
      description={help}
      changed={changed}
      hint={optional ? "Leave it empty to turn it off." : "Must be more than zero."}
      action={<SaveButton disabled={pending || invalid || !dirty} onClick={() => onSave(parsed)} />}
    >
      <UnitInput
        id={inputId}
        unit="minutes"
        placeholder={optional ? "off" : undefined}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
    </SettingCard>
  );
}
